/** @jsxImportSource @emotion/react */
import { OutputData } from "@editorjs/editorjs";
import { css } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
  VFC,
} from "react";
import { useDropzone } from "react-dropzone";
import { createReactEditorJS } from "react-editor-js";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { BUSINESS_CATEGORIES } from "src/constants/businessCategories";
import { CountryPath } from "src/entities/CountryPath";
import { GetArticlesResponse } from "src/entities/GetArticlesResponse";
import { AiArticlePreset } from "src/entities/ai/AiArticlePreset";
import { ArticleV3, ArticlesResponse } from "src/entities/v3/ArticlesResponse";
import { CreditInfo } from "src/entities/v3/AuthMeResponse";
import { GetArticleResponse } from "src/entities/v3/GetArticleResponse";
import { PublisherResponseV3 } from "src/entities/v3/PublisherResponse";
import { Auth } from "src/hooks/useAuth";
import { useCreateArticle } from "src/hooks/useCreateArticle";
import { Refetch, useFetch } from "src/hooks/useFetch";
import { checkSubscripted } from "src/utils/checkSubscripted";
import ComfirmArticleStep from "../components/createArticle/ComfirmArticleStep";
import CreateArticleStep from "../components/createArticle/CreateArticleStep";
import CreateCompleteStep from "../components/createArticle/CreateCompleteStep";
import PaymentArticleStep from "../components/createArticle/PaymentArticleStep";
import CreateArticleModal from "../components/modal/CreateArticleModal";
import FinalConfirmationModal from "../components/modal/FinalConfirmationModal";
import SaveArticleModal from "../components/modal/SaveArticleModal";
import ScheduleArticleModal from "../components/modal/ScheduleArticleModal";
import StepPageWrapper from "../components/page/StepPageWrapper";

interface Props {
  auth: Auth;
  country: CountryPath;
  credits?: number;
  creditInfo?: CreditInfo[];
  publisherResponse?: PublisherResponseV3;
  refetchPublisher: Refetch<PublisherResponseV3>;
  remaining_quota?: number;
  defaultCountry?: string;
}

type Step = "CREATE" | "CONFIRM" | "PAYMENT" | "COMPLETE" | "DISTRIBUTION";

const STEPPER_HEIGHT = 80;

const CreateArticlePage: VFC<Props> = (props: Props) => {
  /**
   * i18n
   */
  const [t] = useTranslation();

  /**
   * Get duplicateFrom parameter from URL for article duplication
   */
  const searchParams = new URLSearchParams(window.location.search);
  const duplicateFromId = searchParams.get("duplicateFrom");

  /**
   * Get AI-generated draft preset passed via location.state (from AiChatPage)
   */
  const location = useLocation();
  const aiPreset = (location.state as { aiPreset?: AiArticlePreset } | null)
    ?.aiPreset;

  /**
   * Step state
   */
  const [currentStep, setCurrentStep] = useState<Step>("CREATE");
  /**
   * Go to confirm step
   */
  const goToConfirmStep = () => {
    setCurrentStep("CONFIRM");
  };
  /**
   * Go back to create step
   */
  const goBackToCreateStep = () => {
    setCurrentStep("CREATE");
  };
  /**
   * Go back to confirm step
   */
  const goBackToConfirmStep = () => {
    setCurrentStep("CONFIRM");
  };
  /**
   * Go to payment step
   */
  const goToPaymentStep = () => {
    setCurrentStep("PAYMENT");
  };
  /**
   * Go to compete step
   */
  const goToCompleteStep = () => {
    setCurrentStep("COMPLETE");
  };
  /**
   * Snackbar
   */
  const { enqueueSnackbar } = useSnackbar();

  /**
   * PR Title settings
   */
  /**
   * PR Title parameters
   */
  const [title, setTitle] = useState("");
  /**
   * Handle the title
   * @param value PR title
   */
  const handleTitleChange = (value: string) => {
    setTitle(value);
  };

  /**
   * PR Summary settings
   */
  /**
   * PR Summary parameters
   */
  const [summary, setSummary] = useState("");
  /**
   * Handle the summary
   * @param value PR summary
   */
  const handleSummaryChange = (value: string) => {
    setSummary(value);
  };

  /**
   * Published at settings
   */
  /**
   * Published at parameters
   */
  const [publishedAt, setPublishedAt] = useState(DateTime.now());
  /**
   * Handle published at
   * @param value post date
   */
  const handlePublishedAtChange = (value: DateTime) => {
    setPublishedAt(value);
  };

  /**
   * Image file hooks
   */
  const { acceptedFiles, getRootProps, getInputProps, isDragAccept } =
    useDropzone({
      multiple: false,
      maxSize: 5 * 1024 * 1024,
      accept: {
        "image/*": [".jpg", ".jpeg", ".png", ".webp", ".gif", ".svg"],
      },
      onDropRejected: (fileRejections) => {
        const rejection = fileRejections[0];
        if (rejection?.errors?.[0]?.code === "file-too-large") {
          enqueueSnackbar("File size exceeds the 5MB limit", {
            variant: "error",
          });
        } else if (rejection?.errors?.[0]?.code === "file-invalid-type") {
          enqueueSnackbar(
            "Invalid file type. Only JPEG, PNG, WebP, GIF, and SVG images are allowed.",
            {
              variant: "error",
            }
          );
        } else {
          enqueueSnackbar("File upload failed", {
            variant: "error",
          });
        }
      },
    });

  /**
   * Thumbnail flag settings
   */
  /**
   * Thumbnail flag parameters
   */
  const [thumbnailAsDefault, setThumbnailAsDefault] = useState(true);
  /**
   * Handle thumbnail flag
   */
  const handleThumbnailAsDefault = (e: ChangeEvent<HTMLInputElement>) => {
    setThumbnailAsDefault(e.target.checked);
  };

  /**
   * Thumbnail URL for duplicated articles
   */
  const [thumbnailUrl, setThumbnailUrl] = useState<string>("");

  /**
   * Track whether duplicate data has been loaded for EditorJS initialization
   */
  const [duplicateDataLoaded, setDuplicateDataLoaded] = useState(
    !duplicateFromId
  );

  /**
   * Track whether AI preset data has been applied for EditorJS initialization
   */
  const [aiPresetLoaded, setAiPresetLoaded] = useState(!aiPreset);

  /**
   * Business categories settings
   */
  /**
   * Business categories parameters
   */
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  /**
   * Handle business categories
   */
  const handleBusinessCategories = (
    _: SyntheticEvent<Element, Event>,
    values: string[]
  ) => {
    if (values.length > 5) {
      return;
    }
    setSelectedCategories(values);
  };

  /**
   * Company Info settings
   */
  /**
   * Company Info parameters
   */
  const [companyInfo, setCompanyInfo] = useState("");
  /**
   * Handle company info
   * @param value company info
   */
  const handleCompanyInfo = (value: string) => {
    setCompanyInfo(value);
  };

  /**
   * Media contact settings
   */
  /**
   * Media contact parameters
   */
  const [mediaContact, setMediaContact] = useState("");
  /**
   * Handle media contact
   * @param value media contact
   */
  const handleMediaContact = (value: string) => {
    setMediaContact(value);
  };

  /**
   * Modal settings
   */
  /**
   * Modal parameters
   */
  const [open, setOpen] = useState(false);
  /**
   * Open the confirmation modal
   */
  const handleClickOpen = () => {
    setOpen(true);
  };
  /**
   * Close the confirmation modal
   */
  const handleClose = () => {
    setOpen(false);
  };

  /**
   * Save Modal settings
   */
  /**
   * Modal parameters
   */
  const [saveOpen, setSaveOpen] = useState(false);
  /**
   * Open the confirmation modal
   */
  const handleClickSaveOpen = () => {
    setSaveOpen(true);
  };
  /**
   * Close the confirmation modal
   */
  const handleSaveClose = () => {
    setSaveOpen(false);
  };

  /**
   * Schedule Modal settings
   */
  /**
   * Modal parameters
   */
  const [scheduleOpen, setScheduleOpen] = useState(false);
  /**
   * Open the schedule modal
   */
  const handleClickScheduleOpen = () => {
    setScheduleOpen(true);
  };
  /**
   * Close the schedule modal
   */
  const handleScheduleClose = () => {
    setScheduleOpen(false);
  };

  /**
   * Final Confirmation Modal settings
   */
  const [finalConfirmOpen, setFinalConfirmOpen] = useState(false);
  const [isScheduledPost, setIsScheduledPost] = useState(false);

  /**
   * Open the final confirmation modal for immediate post
   */
  const handleFinalConfirmOpen = () => {
    setIsScheduledPost(false);
    setFinalConfirmOpen(true);
  };

  /**
   * Open the final confirmation modal for scheduled post
   */
  const handleScheduledFinalConfirmOpen = () => {
    setIsScheduledPost(true);
    setFinalConfirmOpen(true);
  };

  /**
   * Close the final confirmation modal
   */
  const handleFinalConfirmClose = () => {
    setFinalConfirmOpen(false);
  };

  /**
   * Execute the actual article creation after final confirmation
   */
  const handleFinalConfirm = () => {
    handleFinalConfirmClose();
    handleBackDropOpen();
    createArticle("PUBLISHED");
  };

  /**
   * Dropdown settings
   */
  /**
   * Dropdown parameters
   */
  const [backdropOpen, setBackdropOpen] = useState(false);
  /**
   * Open backdrop
   */
  const handleBackDropOpen = () => {
    setBackdropOpen(true);
  };
  /**
   * Close backdrop
   */
  const handleBackdropClose = () => {
    setBackdropOpen(false);
  };

  /**
   * ReactEditor settings
   */
  /**
   * ReactEditor parameters
   */
  const [outputBody, setOutputBody] = useState<OutputData>();
  const handleOutputBody = (outputData: OutputData) => {
    setOutputBody(outputData);
  };
  const editorCore = useRef(null);

  /**
   * ReactEditor settings
   */
  /**
   * ReactEditor parameters
   */
  /* eslint-disable @typescript-eslint/no-unsafe-assignment */
  const ReactEditorJS = createReactEditorJS();

  /**
   * This is the callback function for initializing ReactEditor
   */
  const handleInitialize = useCallback((instance) => {
    editorCore.current = instance;
  }, []);
  /**
   * This is the callback function for save the output body
   */
  const handleSave = useCallback(async () => {
    try {
      await editorCore.current?.save().then((outputData: OutputData) => {
        setOutputBody(outputData);
      });
    } catch {
      // Editor may have been unmounted (e.g., on CONFIRM/PAYMENT step).
      // outputBody already holds the latest saved data, so we can safely ignore.
    }
  }, [setOutputBody]);

  /**
   * API Response
   */
  const [apiResponse, setApiResponse] = useState<GetArticlesResponse>();

  const onSuccess = (response: GetArticlesResponse) => {
    setApiResponse(response);
    goToCompleteStep();
    props.refetchPublisher();
  };

  /**
   * Fetch latest articles for preset selection
   */
  const { data: latestArticles } = useFetch<ArticlesResponse>({
    url: `${process.env.REACT_APP_DOMAIN_V3}/api/v3/articles?country=${props.country}&limit=10&offset=0`,
    skip: !!duplicateFromId,
  });

  /**
   * Fetch source article for duplication
   */
  const { data: duplicateSource } = useFetch<GetArticleResponse>({
    url: `${process.env.REACT_APP_DOMAIN_V3}/api/v3/articles/${duplicateFromId}`,
    skip: !duplicateFromId,
  });

  /**
   * Prefill form when duplicating an article
   */
  useEffect(() => {
    if (duplicateSource && duplicateFromId) {
      setTitle(duplicateSource.title);
      setSummary(duplicateSource.summary);
      setCompanyInfo(duplicateSource.companyInfo);
      setMediaContact(duplicateSource.mediaContact);
      setSelectedCategories(duplicateSource.businessCategories);
      setThumbnailAsDefault(duplicateSource.useThumbnailAsFeaturedImage);
      setThumbnailUrl(duplicateSource.thumbnailImageUrl);
      try {
        const parsedBody = JSON.parse(duplicateSource.body);
        setOutputBody(parsedBody);
      } catch (e) {
        console.error("Failed to parse article body for duplication:", e);
      }
      // Mark duplicate data as loaded so EditorJS can initialize with the data
      setDuplicateDataLoaded(true);
    }
  }, [duplicateSource, duplicateFromId]);

  /**
   * Prefill form when an AI-generated draft is passed via location.state
   */
  useEffect(() => {
    if (!aiPreset) {
      return;
    }
    setTitle(aiPreset.title);
    setSummary(aiPreset.summary);
    // Only keep categories that exist in the master list (autocomplete relies
    // on exact matches), capped at the 5-item limit.
    setSelectedCategories(
      aiPreset.businessCategories
        .filter((c) => (BUSINESS_CATEGORIES as string[]).includes(c))
        .slice(0, 5)
    );
    setOutputBody(aiPreset.body);
    setAiPresetLoaded(true);
    // Clear location.state so a remount does not re-apply and overwrite edits.
    window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiPreset]);

  /**
   * Handle selecting an article to preset companyInfo and mediaContact
   */
  const handleSelectArticlePreset = (article: ArticleV3) => {
    if (article.companyInfo) setCompanyInfo(article.companyInfo);
    if (article.mediaContact) setMediaContact(article.mediaContact);
  };

  /**
   * Create an article
   */
  const { createArticle } = useCreateArticle({
    input: {
      publisherId: props.publisherResponse?.id,
      title: title,
      outputBody: outputBody,
      country: props.country,
      acceptedFiles: acceptedFiles,
      publishedAt: publishedAt,
      businessCategories: BUSINESS_CATEGORIES.map((name) => ({
        id: "",
        name,
        name_en: "",
      })),
      selectedCategories: selectedCategories,
      summary: summary,
      companyInfo: companyInfo,
      mediaContact: mediaContact,
      useThumbnailAsFeaturedImage: thumbnailAsDefault,
      currentThumbnailUrl: thumbnailUrl,
    },
    functions: {
      handleBackDropOpen: handleBackDropOpen,
      handleClose: handleClose,
      handleScheduleClose: handleScheduleClose,
      handleBackdropClose: handleBackdropClose,
      handleSave: handleSave,
      handleComplete: onSuccess,
    },
  });

  useEffect(() => {
    if (currentStep === "PAYMENT") {
      goToConfirmStep();
    }
  }, [props.country]);

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Backdrop sx={{ color: "#fff", zIndex: 10001 }} open={backdropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* create article modal */}
      <CreateArticleModal
        open={open}
        handleClose={handleClose}
        createArticle={async () => {
          await Promise.resolve();
          handleClose();
          handleFinalConfirmOpen();
        }}
        country={props.country}
        handleBackDropOpen={() => {
          // Empty function to satisfy prop requirement
        }}
        is_subscripted={checkSubscripted(props.creditInfo, props.country)}
      />

      {/* schedule article modal */}
      <ScheduleArticleModal
        open={scheduleOpen}
        handleClose={handleScheduleClose}
        createArticle={async () => {
          await Promise.resolve();
          handleScheduleClose();
          handleScheduledFinalConfirmOpen();
        }}
        handleBackDropOpen={() => {
          // Empty function to satisfy prop requirement
        }}
        publishedAt={publishedAt}
        handlePublishedAtChange={handlePublishedAtChange}
        country={props.country}
      />

      {/* save article modal */}
      <SaveArticleModal
        open={saveOpen}
        handleClose={handleSaveClose}
        createArticle={createArticle}
        handleBackDropOpen={handleBackDropOpen}
      />

      {/* final confirmation modal */}
      <FinalConfirmationModal
        open={finalConfirmOpen}
        handleClose={handleFinalConfirmClose}
        handleConfirm={handleFinalConfirm}
        title={title}
        outputBody={outputBody}
        country={props.country}
        isScheduled={isScheduledPost}
        scheduledDate={isScheduledPost ? publishedAt : undefined}
      />

      {/* Content */}
      <StepPageWrapper>
        {currentStep === "CREATE" ||
        currentStep === "CONFIRM" ||
        currentStep === "PAYMENT" ? (
          <div css={stepperContainer}>
            {currentStep === "CONFIRM" ? (
              <div css={stepperReturnButtonWrapper}>
                <button
                  css={stepperReturnButton}
                  type="button"
                  onClick={goBackToCreateStep}
                >
                  <img
                    width={20}
                    css={stepContent}
                    alt="step1_gray"
                    src="/articleEditor/arrow.png"
                  />
                  <img
                    width={20}
                    css={stepContent}
                    alt="step1_gray"
                    src="/step/step_1_gray.png"
                  />
                  <div>{t("create_press_release.button.create")}</div>
                </button>
              </div>
            ) : null}
            {currentStep === "PAYMENT" ? (
              <div css={stepperReturnButtonWrapper}>
                <button
                  css={stepperReturnButton}
                  type="button"
                  onClick={goBackToConfirmStep}
                >
                  <img
                    width={20}
                    css={stepContent}
                    alt="step2_gray"
                    src="/articleEditor/arrow.png"
                  />
                  <img
                    width={20}
                    css={stepContent}
                    alt="step2_gray"
                    src="/step/step_2_gray.png"
                  />
                  <div>{t("create_press_release.button.confirm")}</div>
                </button>
              </div>
            ) : null}
            <div css={stepperWrapper}>
              <div css={currentStep === "CREATE" ? step : disabledStep}>
                {currentStep === "CREATE" ? (
                  <img
                    width={30}
                    alt="step1_blue"
                    src="/step/step_1_blue.png"
                  />
                ) : (
                  <img
                    width={30}
                    alt="step1_gray"
                    src="/step/step_1_gray.png"
                  />
                )}
                {t("create_press_release.button.create")}
              </div>
              <div css={currentStep === "CONFIRM" ? step : disabledStep}>
                {currentStep === "CONFIRM" ? (
                  <img
                    width={30}
                    alt="step2_blue"
                    src="/step/step_2_blue.png"
                  />
                ) : (
                  <img
                    width={30}
                    alt="step2_gray"
                    src="/step/step_2_gray.png"
                  />
                )}
                {t("create_press_release.button.confirm")}
              </div>
              <div css={currentStep === "PAYMENT" ? step : disabledStep}>
                {currentStep === "PAYMENT" ? (
                  <img
                    width={30}
                    alt="step2_blue"
                    src="/step/step_3_blue.png"
                  />
                ) : (
                  <img
                    width={30}
                    alt="step2_gray"
                    src="/step/step_3_gray.png"
                  />
                )}
                {t("dashboard.modal.button.payment")}
              </div>
            </div>
          </div>
        ) : null}
        {currentStep === "CREATE" ? (
          <CreateArticleStep
            // common
            auth={props.auth}
            country={props.country}
            publisherResponse={props.publisherResponse}
            ReactEditorJS={ReactEditorJS}
            // article information
            publishedAt={publishedAt}
            title={title}
            summary={summary}
            selectedCategories={selectedCategories}
            thumbnailAsDefault={thumbnailAsDefault}
            companyInfo={companyInfo}
            mediaContact={mediaContact}
            outputBody={outputBody}
            businessCategories={BUSINESS_CATEGORIES.map((name) => ({
              id: "",
              name,
              name_en: "",
            }))}
            thumbnailUrl={thumbnailUrl}
            isDuplicating={!!duplicateFromId || !!aiPreset}
            duplicateDataLoaded={duplicateDataLoaded && aiPresetLoaded}
            latestArticles={latestArticles}
            handleSelectArticlePreset={handleSelectArticlePreset}
            // dropzone param
            acceptedFiles={acceptedFiles}
            isDragAccept={isDragAccept}
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            // functions
            handlePublishedAtChange={handlePublishedAtChange}
            handleBusinessCategories={handleBusinessCategories}
            handleTitleChange={handleTitleChange}
            handleSummaryChange={handleSummaryChange}
            handleThumbnailAsDefault={handleThumbnailAsDefault}
            handleOutputBody={handleOutputBody}
            handleInitialize={handleInitialize}
            handleSave={handleSave}
            handleCompanyInfo={handleCompanyInfo}
            handleMediaContact={handleMediaContact}
            handleClickSaveOpen={handleClickSaveOpen}
            goToConfirmStep={goToConfirmStep}
          />
        ) : null}
        {currentStep === "CONFIRM" ? (
          <ComfirmArticleStep
            // common
            publisherResponse={props.publisherResponse}
            country={props.country}
            // article information
            publishedAt={publishedAt}
            title={title}
            summary={summary}
            selectedCategories={selectedCategories}
            thumbnailAsDefault={thumbnailAsDefault}
            companyInfo={companyInfo}
            mediaContact={mediaContact}
            outputBody={outputBody}
            businessCategories={BUSINESS_CATEGORIES.map((name) => ({
              id: "",
              name,
              name_en: "",
            }))}
            thumbnailUrl={thumbnailUrl}
            // Dropzone param
            acceptedFiles={acceptedFiles}
            // functions
            handleSave={handleSave}
            handleClickSaveOpen={handleClickSaveOpen}
            goToPaymentStep={goToPaymentStep}
          />
        ) : null}
        {currentStep === "PAYMENT" ? (
          <PaymentArticleStep
            // common
            credits={props.credits}
            title={title}
            is_subscripted={checkSubscripted(props.creditInfo, props.country)}
            remaining_quota={props.remaining_quota}
            thumbnailUrl={thumbnailUrl}
            // Dropzone param
            acceptedFiles={acceptedFiles}
            // functions
            handleSave={handleSave}
            handleClickSaveOpen={handleClickSaveOpen}
            handleClickOpen={handleClickOpen}
            handleClickScheduleOpen={handleClickScheduleOpen}
            defaultCountry={props.defaultCountry}
          />
        ) : null}
        {currentStep === "COMPLETE" ? (
          <CreateCompleteStep
            apiResponse={apiResponse}
            country={props.country}
            publisherResponse={props.publisherResponse}
          />
        ) : null}
      </StepPageWrapper>
    </LocalizationProvider>
  );
};

const stepperContainer = css`
  display: flex;
  justify-content: center;
  position: relative;
  background: #eaeaea;
  height: ${STEPPER_HEIGHT}px;
`;

const stepperWrapper = css`
  display: flex;
  margin-top: 12px;
  @media (max-width: 640px) {
    width: 100%;
    margin-top: 8px;
  }
`;

const step = css`
  background: #ffffff;
  padding: 4px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 180px;
  color: #0045ff;
  @media (max-width: 640px) {
    width: auto;
    flex: 1 1 0;
    min-width: 0;
    padding: 4px 6px;
    font-size: 11px;
    text-align: center;
    & img {
      width: 22px;
    }
  }
`;

const disabledStep = css`
  padding: 4px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 180px;
  color: #969696;
  @media (max-width: 640px) {
    width: auto;
    flex: 1 1 0;
    min-width: 0;
    padding: 4px 6px;
    font-size: 11px;
    text-align: center;
    & img {
      width: 22px;
    }
  }
`;

const stepperReturnButtonWrapper = css`
  position: absolute;
  left: 24px;
  top: 26px;
  @media (max-width: 640px) {
    left: 8px;
    top: 30px;
    font-size: 11px;
  }
`;

const stepperReturnButton = css`
  display: flex;
  align-items: center;
`;

const stepContent = css`
  margin-right: 8px;
`;

export default CreateArticlePage;
